/**
 * SDL Settings Screen
 *
 * Four tabs: Audio, Graphics, Account, Support.
 *
 * - Audio + Graphics write directly to the SettingsStore singleton, which
 *   persists to localStorage and emits `change`. `main.ts` subscribes and
 *   applies the values to the live AudioManager / SharedRenderer — this screen
 *   never touches the engine itself.
 * - Account + Support actions emit events (`account:*`, `support:*`) that
 *   `main.ts` wires to auth/CDN/server flows. No email/password management:
 *   accounts only connect via Apple or Google, so there is nothing to update
 *   or recover here.
 *
 * Scoped styles live in styles/settings.css under `.settings-screen`.
 */

import { Capacitor } from '@capacitor/core';
import { EventEmitter } from '../../engine/core/EventEmitter';
import { settingsStore, type MicMode } from '../../settings/SettingsStore';
import { serverConfig } from '../../network/ServerConfig';
import { notificationService } from '../../services/NotificationService';
import { showToast, showAnchoredToast } from '../components/Toast';
import type { RenderQuality } from '../../engine/render/SharedRenderer';
import type { BlockedPlayer, UsernameChangeResultPayload } from '@sdl/shared';
import { sanitizeUsername, isReservedUsername } from '@sdl/shared';
import {
  submitFeedback, createTicket, listTickets, getTicket, replyTicket,
  closeTicket, reopenTicket,
  type TicketSummary, type TicketDetail, type TicketStatus,
} from '../../api/supportApi';

/** A picked-but-not-yet-uploaded attachment (base64 data URL + display chip). */
interface AttachmentDraft { dataUrl: string; name: string; kind: 'image' | 'video'; }

/** Client-side attachment caps (mirrored on the server). */
const MAX_ATTACHMENTS = 4;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const MAX_VIDEO_BYTES = 50 * 1024 * 1024;

const TICKET_STATUS_LABEL: Record<TicketStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

/** Legal pages are served by main-server (see its /privacy and /terms routes),
 *  so build the URLs from the configured server origin — works in dev + prod. */
const LEGAL_BASE = serverConfig.mainServerUrl.replace(/\/$/, '');

export type SettingsTab = 'audio' | 'graphics' | 'account' | 'support';

/** Local chat storage numbers, provided by main.ts (chatService owns the DB). */
export interface ChatStorageInfo {
  /** Approximate bytes of chat data (text + photos) on this device. */
  chatBytes: number;
  /** Bytes held by chat photos alone. */
  imageBytes: number;
  messageCount: number;
}

export interface SettingsAccount {
  username: string;
  email: string;
  /** Avatar URL. Empty string falls back to initials. */
  picture: string;
  /** Guest accounts can't change email and should be nudged to register. */
  isGuest: boolean;
  /** True until the account's first rename is spent — the first one is free. */
  nameChangeFree: boolean;
}

/** Live availability/validation states for the name editor. */
type NameStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid' | 'same' | 'reserved';

export interface SettingsScreenEvents {
  'account:change-picture': void;
  /** Player confirmed a new handle (already sanitized client-side). main.ts
   *  fires the server change and feeds the outcome back via onUsernameChangeResult. */
  'account:change-username': { username: string };
  /** Guest tapped a provider in the "Connect & bag 100M" section. */
  'account:connect': { provider: 'google' | 'apple' };
  'account:logout': void;
  'account:unblock': { playerId: string };
  /** Player asked to wipe all local chat history (messages + photos). */
  'account:clear-chat': void;
  'support:open-ticket': { subject: string; message: string };
  'support:feedback': { message: string };
  /** Player tapped "Rate us". main.ts opens the store listing. */
  'support:rate': void;
  /** Unread-reply state changed. main.ts badges the settings entry point. */
  'support:unread-changed': { hasUnread: boolean };
  /** Player flipped the push-notifications switch. main.ts drives the OS
   *  permission prompt / token (un)registration from this. */
  'notifications:toggle': { enabled: boolean };
  [key: string]: unknown;
}

const QUALITY_INFO: Record<RenderQuality, { label: string; blurb: string }> = {
  low: { label: 'LOW', blurb: 'Best battery & frame rate. For older phones.' },
  medium: { label: 'MEDIUM', blurb: 'Balanced visuals and performance.' },
  high: { label: 'HIGH', blurb: 'Full shadows & effects. Newer phones.' },
};

const APP_VERSION = (import.meta.env?.VITE_APP_VERSION as string) ?? '0.1.0-beta';

/** Connect-account bonus, formatted for display. Mirrors server
 *  config.game.connectBonus.stacks (100M). */
const CONNECT_BONUS_DISPLAY = '100,000,000';

/** Gold price of a username change. Mirrors server config.game.usernameChange
 *  .goldCost — display only; the server is authoritative on the charge. */
const USERNAME_CHANGE_GOLD_COST = 50;

// Provider button glyphs — shared with the login screen markup so the in-app
// "connect account" buttons read identically to the first-run sign-in.
const APPLE_GLYPH = `
  <svg width="18" height="22" viewBox="0 0 18 22" fill="none">
    <path d="M17.0703 17.1562C16.7422 17.9141 16.3555 18.6133 15.9082 19.2598C15.2949 20.1445 14.7949 20.7676 14.4141 21.1289C13.8281 21.6992 13.1992 21.9922 12.5234 22.0098C12.043 22.0098 11.4609 21.873 10.7832 21.5957C10.1035 21.3203 9.47656 21.1836 8.90039 21.1836C8.29688 21.1836 7.65234 21.3203 6.96484 21.5957C6.27734 21.873 5.71875 22.0176 5.28516 22.0332C4.63672 22.0625 3.99219 21.7617 3.34961 21.1289C2.93945 20.7402 2.41992 20.0977 1.79297 19.2012C1.12109 18.2441 0.570312 17.1328 0.140625 15.8652C-0.320312 14.4902 -0.554688 13.1582 -0.554688 11.8672C-0.554688 10.3945 -0.240234 9.12109 0.390625 8.05078C0.882812 7.19141 1.53516 6.53125 2.35156 6.06836C3.16797 5.60547 4.04883 5.36914 4.99805 5.35352C5.50781 5.35352 6.17578 5.51172 7.00586 5.82227C7.83398 6.13379 8.37305 6.29199 8.62109 6.29199C8.80859 6.29199 9.40625 6.10547 10.4004 5.73438C11.3379 5.39062 12.1309 5.24805 12.7832 5.30273C14.5527 5.44531 15.8789 6.14844 16.7559 7.41602C15.1758 8.37109 14.3945 9.72266 14.4102 11.4648C14.4258 12.8398 14.9238 13.9844 15.9004 14.8926C16.3379 15.3105 16.8262 15.6328 17.3711 15.8594C17.2773 16.1172 17.1777 16.3652 17.0703 16.6035V17.1562ZM12.8906 0.410156C12.8906 1.49609 12.4902 2.51172 11.6953 3.44922C10.7344 4.5625 9.57422 5.20312 8.31641 5.09961C8.30078 4.96875 8.29102 4.83008 8.29102 4.68359C8.29102 3.64258 8.74805 2.52734 9.55859 1.61523C9.96289 1.15234 10.4746 0.771484 11.0938 0.472656C11.7109 0.177734 12.2949 0.0136719 12.8438 -0.00195312C12.8594 0.138672 12.8906 0.275391 12.8906 0.410156Z" fill="black"/>
  </svg>`;

const GOOGLE_GLYPH = `
  <svg viewBox="0 0 24 24" width="18" height="18">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>`;

export class SettingsScreen extends EventEmitter<SettingsScreenEvents> {
  public element: HTMLElement;
  private activeTab: SettingsTab = 'account';
  /** Cached OS notification permission (granted?) — the toggle shows ON only
   *  when the OS permission is actually granted AND the player hasn't muted it.
   *  Refreshed asynchronously whenever the Account tab renders. */
  private notifGranted = false;
  private account: SettingsAccount = {
    username: 'Player',
    email: '',
    picture: '',
    isGuest: true,
    nameChangeFree: true,
  };
  private blocked: BlockedPlayer[] = [];
  private blockedExpanded = false;
  private connectExpanded = false;

  // ── Username editor state ──
  private nameEditing = false;
  private nameDraft = '';                 // the live (sanitized) candidate
  private nameStatus: NameStatus = 'idle';
  private nameSubmitting = false;
  /** Injected by main.ts — reuses the shared DebouncedUsernameChecker so the
   *  Settings editor gets the exact same debounce + stale-drop behavior as the
   *  sign-up name prompt. */
  private usernameCheck: ((name: string, cb: (available: boolean) => void) => void) | null = null;
  private usernameCheckCancel: (() => void) | null = null;
  // Live refs to the open editor's nodes (updated in place so a status change
  // doesn't rebuild the panel and steal focus mid-type).
  private nameInputEl: HTMLInputElement | null = null;
  private nameStatusEl: HTMLElement | null = null;
  private nameConfirmBtn: HTMLButtonElement | null = null;
  /** Safety net: if the server never answers a change, un-stick "Saving...". */
  private nameSubmitTimer: ReturnType<typeof setTimeout> | null = null;

  /** Injected by main.ts — reads chat storage stats off the chat DB. */
  private chatStorageProvider: (() => Promise<ChatStorageInfo | null>) | null = null;

  // Support state
  private supportToken: string | null = null;
  private myTickets: TicketSummary[] = [];
  private supportThread: TicketDetail | null = null; // non-null = thread view open
  private newTicketFiles: AttachmentDraft[] = [];
  private replyFiles: AttachmentDraft[] = [];

  private tabBar!: HTMLElement;
  private panel!: HTMLElement;

  constructor() {
    super();
    this.element = this.createElement();
    this.renderPanel();
  }

  /** Push live account info (called from main.ts on auth:success). */
  setAccount(account: Partial<SettingsAccount>): void {
    this.account = { ...this.account, ...account };
    if (this.activeTab === 'account') this.renderPanel();
  }

  /** Wire the chat storage stats source (main.ts → chatService). */
  setChatStorageProvider(provider: () => Promise<ChatStorageInfo | null>): void {
    this.chatStorageProvider = provider;
    if (this.activeTab === 'account') this.renderPanel();
  }

  /**
   * Wire the live availability checker (main.ts injects the shared
   * DebouncedUsernameChecker). `check` schedules a debounced query and calls
   * back with availability; `cancel` drops any pending/in-flight query.
   */
  setUsernameChecker(
    check: (name: string, cb: (available: boolean) => void) => void,
    cancel: () => void,
  ): void {
    this.usernameCheck = check;
    this.usernameCheckCancel = cancel;
  }

  /**
   * Feed the server's USERNAME_CHANGE outcome back into the editor. Success
   * collapses the editor onto the new name; failure re-enables it with an
   * inline reason. (main.ts owns the toast + home/lobby refresh.)
   */
  onUsernameChangeResult(result: UsernameChangeResultPayload): void {
    if (this.nameSubmitTimer) { clearTimeout(this.nameSubmitTimer); this.nameSubmitTimer = null; }
    this.nameSubmitting = false;
    if (result.ok && result.username) {
      // First free change is spent → subsequent ones cost Gold.
      this.account = { ...this.account, username: result.username, nameChangeFree: false };
      this.closeNameEditor();
      return;
    }
    switch (result.reason) {
      case 'taken': this.nameStatus = 'taken'; break;
      case 'invalid': this.nameStatus = 'invalid'; break;
      case 'reserved': this.nameStatus = 'reserved'; break;
      case 'unchanged': this.nameStatus = 'same'; break;
      default: this.nameStatus = 'available'; break; // insufficient_gold / guest / rate_limited / error → main.ts toasts; allow retry
    }
    if (this.activeTab === 'account') this.renderPanel();
  }

  /** Provide the player JWT used for support REST calls (main.ts auth:success). */
  setSupportToken(token: string | null): void {
    this.supportToken = token;
    if (token) void this.refreshTickets();
  }

  /** True if any ticket has an unread admin reply (drives the entry-point badge). */
  hasUnreadTickets(): boolean {
    return this.myTickets.some((t) => t.unreadForPlayer);
  }

  /**
   * Refresh the player's ticket list. Re-renders the Support tab if visible and
   * emits `support:unread-changed` so main.ts can badge the settings entry point.
   */
  async refreshTickets(): Promise<void> {
    if (!this.supportToken) return;
    const tickets = await listTickets(this.supportToken);
    this.myTickets = tickets;
    if (this.activeTab === 'support' && !this.supportThread) this.renderPanel();
    this.updateSupportTabBadge();
    this.emit('support:unread-changed', { hasUnread: this.hasUnreadTickets() });
  }

  /** Push the live blocked list (from BlockService). Re-renders if visible. */
  setBlocked(blocked: BlockedPlayer[]): void {
    this.blocked = blocked;
    if (this.activeTab === 'account') this.renderPanel();
  }

  /** Drop any open rename editor without re-rendering (caller renders). */
  private resetNameEditorState(): void {
    this.usernameCheckCancel?.();
    if (this.nameSubmitTimer) { clearTimeout(this.nameSubmitTimer); this.nameSubmitTimer = null; }
    this.nameEditing = false;
    this.nameSubmitting = false;
    this.nameStatus = 'idle';
    this.nameDraft = '';
    this.nameInputEl = null;
    this.nameStatusEl = null;
    this.nameConfirmBtn = null;
  }

  /** Called when the screen is navigated away from — drop any open rename
   *  editor (and its pending check/timer) so we don't leave a focused input or
   *  in-flight work behind the slide. */
  onHide(): void {
    this.resetNameEditorState();
  }

  /** Called when the screen becomes visible — re-sync controls to store. */
  onShow(): void {
    this.supportThread = null; // always land on the ticket list, not a stale thread
    this.resetNameEditorState();
    this.renderPanel();
    if (this.supportToken) void this.refreshTickets();
  }

  /** Open a specific tab (e.g. deep-link from the home avatar → Account). */
  openTab(tab: SettingsTab): void {
    this.activeTab = tab;
    if (tab === 'support') this.supportThread = null; // land on the list
    this.tabBar.querySelectorAll<HTMLElement>('.settings-tab').forEach((el) => {
      el.classList.toggle('active', el.dataset.tab === tab);
    });
    this.renderPanel();
    if (tab === 'support' && this.supportToken) void this.refreshTickets();
  }

  private createElement(): HTMLElement {
    const screen = document.createElement('div');
    screen.className = 'settings-screen';
    screen.innerHTML = `
      <div class="settings-topbar">
        <div class="settings-title">SETTINGS</div>
        <div class="settings-subtitle">TUNE YOUR GAME</div>
      </div>
      <div class="settings-tabs"></div>
      <div class="settings-panel"></div>
    `;

    this.tabBar = screen.querySelector('.settings-tabs')!;
    this.panel = screen.querySelector('.settings-panel')!;

    const tabs: Array<{ id: SettingsTab; label: string }> = [
      { id: 'account', label: 'Account' },
      { id: 'audio', label: 'Audio' },
      { id: 'graphics', label: 'Graphics' },
      { id: 'support', label: 'Support' },
    ];

    for (const { id, label } of tabs) {
      const btn = document.createElement('button');
      btn.className = 'settings-tab';
      btn.dataset.tab = id;
      btn.textContent = label;
      if (id === this.activeTab) btn.classList.add('active');
      btn.addEventListener('click', () => this.selectTab(id));
      this.tabBar.appendChild(btn);
    }

    return screen;
  }

  private selectTab(tab: SettingsTab): void {
    if (tab === this.activeTab) return;
    this.resetNameEditorState(); // leaving the account tab abandons any open rename
    this.activeTab = tab;
    this.tabBar.querySelectorAll<HTMLElement>('.settings-tab').forEach((el) => {
      el.classList.toggle('active', el.dataset.tab === tab);
    });
    this.renderPanel();
  }

  /**
   * Re-query the live OS notification permission and refresh the toggle to
   * match. Called after a toggle action (from main.ts) and on every Account-tab
   * render. Only re-renders when the value actually changed, so there's no loop.
   */
  async refreshNotificationState(): Promise<void> {
    const granted = (await notificationService.getPermission()) === 'granted';
    if (granted !== this.notifGranted) {
      this.notifGranted = granted;
      if (this.activeTab === 'account') this.renderPanel();
    }
  }

  private renderPanel(): void {
    this.panel.innerHTML = '';
    switch (this.activeTab) {
      case 'audio': this.renderAudio(); break;
      case 'graphics': this.renderGraphics(); break;
      case 'account': this.renderAccount(); break;
      case 'support': this.renderSupport(); break;
    }
  }

  // ── Audio ──────────────────────────────────────────────────────────────

  private renderAudio(): void {
    // The Settings-tab path re-renders the whole panel on mute toggle (so the
    // tab's other groups stay correct); the detached path re-renders only
    // itself. Both build the same groups via renderAudioInto.
    this.renderAudioInto(this.panel, () => {
      if (this.activeTab === 'audio') this.renderPanel();
    });
  }

  /**
   * Build a standalone copy of the Audio panel (Sound + Voice groups) for
   * embedding OUTSIDE the Settings screen — e.g. the in-game menu's Sound view.
   * Wired to the same settingsStore, so it stays in sync with the Settings tab.
   *
   * The wrapper carries `.settings-screen` only to inherit that scope's CSS
   * variables; `.settings-audio-detached` neutralises the full-screen layout.
   * The returned element holds no SettingsScreen state — the caller owns it and
   * should drop it (remove from the DOM) when its host closes; a fresh one is
   * handed out on the next call.
   */
  buildAudioPanel(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'settings-screen settings-audio-detached';
    const render = (): void => this.renderAudioInto(wrap, render);
    render();
    return wrap;
  }

  /** Shared builder for both the Settings tab and the detached panel. */
  private renderAudioInto(target: HTMLElement, rerender: () => void): void {
    target.innerHTML = '';
    const a = settingsStore.get('audio');
    const group = this.group('Sound');

    group.appendChild(this.toggleRow('Mute all', a.muted, (v) => {
      settingsStore.update('audio', { muted: v });
      // Re-render so the disabled state on sliders reflects the mute.
      rerender();
    }));

    const disabled = a.muted;
    group.appendChild(this.sliderRow('Master', a.master, disabled, (v) => settingsStore.update('audio', { master: v })));
    group.appendChild(this.sliderRow('Music', a.music, disabled, (v) => settingsStore.update('audio', { music: v })));
    group.appendChild(this.sliderRow('Game FX', a.sfx, disabled, (v) => settingsStore.update('audio', { sfx: v })));
    group.appendChild(this.sliderRow('UI FX', a.ui, disabled, (v) => settingsStore.update('audio', { ui: v })));
    target.appendChild(group);

    const voice = this.group('Voice Chat');
    voice.appendChild(this.toggleRow('Enable mic', a.voiceChatEnabled, (v) => settingsStore.update('audio', { voiceChatEnabled: v })));
    voice.appendChild(this.sliderRow('Voice volume', a.voice, disabled, (v) => settingsStore.update('audio', { voice: v })));

    // Mic-button behavior: hold to talk (with tap-latch) vs plain open-mic
    // toggle. main.ts pushes the change into the overlay via applySettings.
    const micSeg = document.createElement('div');
    micSeg.className = 'settings-segmented';
    micSeg.dataset.sound = 'button';
    ([['push', 'Hold to Talk'], ['open', 'Open Mic']] as [MicMode, string][]).forEach(([mode, label]) => {
      const btn = document.createElement('button');
      btn.className = 'settings-seg-btn';
      btn.textContent = label;
      btn.classList.toggle('active', a.micMode === mode);
      btn.addEventListener('click', () => {
        settingsStore.update('audio', { micMode: mode });
        rerender();
      });
      micSeg.appendChild(btn);
    });
    voice.appendChild(micSeg);

    const micBlurb = document.createElement('div');
    micBlurb.className = 'settings-blurb';
    micBlurb.textContent = a.micMode === 'open'
      ? 'Your mic stays on while you play. Tap the mic in a game to turn it off.'
      : 'Hold the mic in a game to talk. A quick tap switches to Open Mic.';
    voice.appendChild(micBlurb);
    target.appendChild(voice);
  }

  // ── Graphics ───────────────────────────────────────────────────────────

  private renderGraphics(): void {
    const g = settingsStore.get('graphics');
    const group = this.group('Quality');

    const seg = document.createElement('div');
    seg.className = 'settings-segmented';
    seg.dataset.sound = 'button'; // LOW/MEDIUM/HIGH select buttons → button press
    (['low', 'medium', 'high'] as RenderQuality[]).forEach((q) => {
      const btn = document.createElement('button');
      btn.className = 'settings-seg-btn';
      btn.textContent = QUALITY_INFO[q].label;
      btn.classList.toggle('active', g.quality === q);
      btn.addEventListener('click', () => {
        settingsStore.update('graphics', { quality: q });
        if (this.activeTab === 'graphics') this.renderPanel();
      });
      seg.appendChild(btn);
    });
    group.appendChild(seg);

    const blurb = document.createElement('div');
    blurb.className = 'settings-blurb';
    blurb.textContent = QUALITY_INFO[g.quality].blurb;
    group.appendChild(blurb);
    this.panel.appendChild(group);

    const chat = settingsStore.get('chat');
    const display = this.group('Display');
    display.appendChild(this.toggleRow('Chat bubbles', chat.bubblesEnabled, (v) => settingsStore.update('chat', { bubblesEnabled: v })));
    const chatBlurb = document.createElement('div');
    chatBlurb.className = 'settings-blurb';
    chatBlurb.textContent = 'Show floating chat over players in the lobby and games.';
    display.appendChild(chatBlurb);
    this.panel.appendChild(display);
  }

  // ── Account ────────────────────────────────────────────────────────────

  private renderAccount(): void {
    const { username, email, picture, isGuest } = this.account;

    const card = document.createElement('div');
    card.className = 'settings-account-card';
    const avatar = picture
      ? `<img class="settings-avatar" src="${picture}" alt="">`
      : `<div class="settings-avatar settings-avatar-fallback">${this.initials(username)}</div>`;
    card.innerHTML = `
      ${avatar}
      <div class="settings-account-meta">
        <div class="settings-account-name">${this.escape(username)}</div>
        <div class="settings-account-email">${isGuest ? 'Guest account' : this.escape(email || 'No email set')}</div>
      </div>
    `;
    const photoBtn = document.createElement('button');
    photoBtn.className = 'settings-btn settings-btn-cyan';
    photoBtn.textContent = 'Change Photo';
    photoBtn.addEventListener('click', () => this.emit('account:change-picture', undefined));
    card.appendChild(photoBtn);
    this.panel.appendChild(card);

    // Player name — view current handle + open the rename editor (costs Gold).
    this.panel.appendChild(this.renderPlayerName());

    // Order under the profile card: Connect (guests) → Push Notifications →
    // Privacy → Log out. Signed-in accounts get no email/password rows:
    // auth is Apple/Google only, so there is nothing to update or recover.
    if (isGuest) {
      // Guests get the expandable "connect & bag 100M" section — the bonus is
      // the hook that drives sign-ups, so advertise it on the row itself.
      this.panel.appendChild(this.renderConnect());
    }

    // Notifications — account-level preference. Shown ON only when the OS
    // permission is actually granted AND the player hasn't muted it, so the
    // switch never falsely reads ON before the player has accepted.
    const notif = settingsStore.get('notifications');
    const notifGroup = this.group('Notifications');
    notifGroup.appendChild(this.toggleRow('Push notifications', this.notifGranted && notif.enabled, (v) => {
      settingsStore.update('notifications', { enabled: v });
      // main.ts drives the OS prompt (on) / token unregister (off), then calls
      // refreshNotificationState() so this toggle re-reads the real permission.
      this.emit('notifications:toggle', { enabled: v });
    }));
    const notifBlurb = document.createElement('div');
    notifBlurb.className = 'settings-blurb';
    notifBlurb.textContent = 'Get pinged when your crew messages you and when your free Stacks are ready.';
    notifGroup.appendChild(notifBlurb);
    this.panel.appendChild(notifGroup);
    // Reconcile the toggle with the live OS permission (async; re-renders only
    // if it differs from the cached value, so no render loop).
    void this.refreshNotificationState();

    // Privacy — policy link + blocked players accordion.
    this.panel.appendChild(this.renderBlocked());

    // Chat storage — messages and photos live on this device, not our
    // servers, so surface how much room they take and a way to reclaim it.
    if (this.chatStorageProvider) {
      this.panel.appendChild(this.renderChatStorage());
    }

    const danger = this.group('');
    danger.appendChild(this.actionRow('Log out', '', 'danger', () => this.emit('account:logout', undefined)));
    this.panel.appendChild(danger);
  }

  /**
   * "Chat Storage" group: local chat data footprint + device free space +
   * a clear-history action. Numbers load async and fill in place.
   */
  private renderChatStorage(): HTMLElement {
    const group = this.group('Chat Storage');

    const usageRow = document.createElement('div');
    usageRow.className = 'settings-row';
    usageRow.innerHTML = `
      <span class="settings-row-label">Chat data on this device</span>
      <span class="settings-slider-val" data-chat-usage>...</span>
    `;
    group.appendChild(usageRow);

    const deviceRow = document.createElement('div');
    deviceRow.className = 'settings-row';
    deviceRow.innerHTML = `
      <span class="settings-row-label">Device space available</span>
      <span class="settings-slider-val" data-device-free>...</span>
    `;
    group.appendChild(deviceRow);

    const blurb = document.createElement('div');
    blurb.className = 'settings-blurb';
    blurb.textContent = 'Your messages and photos are stored on this phone only. When space runs low the oldest photos are cleared automatically.';
    group.appendChild(blurb);

    group.appendChild(this.actionRow('Clear chat history', 'Deletes all messages and photos on this device', 'danger', () => {
      this.emit('account:clear-chat', undefined);
    }));

    // Fill numbers asynchronously (the group is already on screen).
    void (async () => {
      const usageEl = usageRow.querySelector<HTMLElement>('[data-chat-usage]')!;
      const freeEl = deviceRow.querySelector<HTMLElement>('[data-device-free]')!;
      try {
        const info = await this.chatStorageProvider?.();
        usageEl.textContent = info
          ? `${this.formatBytes(info.chatBytes)} · ${info.messageCount} messages`
          : 'Unavailable';
      } catch {
        usageEl.textContent = 'Unavailable';
      }
      try {
        const est = await navigator.storage?.estimate?.();
        if (est?.quota != null && est.usage != null) {
          freeEl.textContent = this.formatBytes(Math.max(0, est.quota - est.usage));
        } else {
          freeEl.textContent = 'Unavailable';
        }
      } catch {
        freeEl.textContent = 'Unavailable';
      }
    })();

    return group;
  }

  /** Re-runs the async fill (call after a clear so the row updates). */
  refreshChatStorage(): void {
    if (this.activeTab === 'account') this.renderPanel();
  }

  private formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }

  /**
   * Guest-only "Connect & bag 100M" section. The header row advertises the
   * bonus; tapping it expands in place (like the Blocked Players accordion) to
   * reveal the Apple/Google buttons. Each provider emits `account:connect`,
   * which main.ts turns into the social sign-in + guest-link + 100M claim.
   */
  private renderConnect(): HTMLElement {
    const group = this.group('Account');

    const header = document.createElement('button');
    header.className = 'settings-row settings-action-row accent-gold';
    header.innerHTML = `
      <div class="settings-action-text">
        <span class="settings-row-label">Connect &amp; bag 100M</span>
        <span class="settings-row-hint">Save your progress &amp; claim ${CONNECT_BONUS_DISPLAY}</span>
      </div>
      <span class="settings-chevron">${this.connectExpanded ? '⌄' : '›'}</span>
    `;
    header.addEventListener('click', () => {
      this.connectExpanded = !this.connectExpanded;
      if (this.activeTab === 'account') this.renderPanel();
    });
    group.appendChild(header);

    if (!this.connectExpanded) return group;

    const body = document.createElement('div');
    body.className = 'settings-connect-body';

    const hook = document.createElement('div');
    hook.className = 'settings-connect-hook';
    hook.innerHTML = `
      <span class="settings-connect-coin">💰</span>
      <span>Link an account to claim <b>${CONNECT_BONUS_DISPLAY}</b> Stacks — and never lose your dice or progress.</span>
    `;
    body.appendChild(hook);

    const apple = document.createElement('button');
    apple.className = 'login-btn-social login-btn-apple';
    apple.innerHTML = `${APPLE_GLYPH} Sign in with Apple`;
    apple.addEventListener('click', () => this.emit('account:connect', { provider: 'apple' }));
    body.appendChild(apple);

    const google = document.createElement('button');
    google.className = 'login-btn-social login-btn-google';
    google.innerHTML = `${GOOGLE_GLYPH} Sign in with Google`;
    google.addEventListener('click', () => this.emit('account:connect', { provider: 'google' }));
    body.appendChild(google);

    group.appendChild(body);
    return group;
  }

  // ── Player name (rename) ─────────────────────────────────────────────────

  /**
   * "Player Name" group. Collapsed = current handle + a Change affordance.
   * Expanded = an inline editor with the SAME live availability check as the
   * sign-up prompt, plus the Gold cost on the confirm button.
   */
  private renderPlayerName(): HTMLElement {
    const group = this.group('Player Name');

    // Guests can't rename — nudge them to connect an account instead.
    if (this.account.isGuest) {
      const row = document.createElement('div');
      row.className = 'settings-row settings-action-row settings-name-locked';
      row.innerHTML = `
        <div class="settings-action-text">
          <span class="settings-row-label settings-name-preview">${this.escape(this.account.username)}</span>
          <span class="settings-row-hint">Connect an account to change your name</span>
        </div>
        <span class="settings-name-change locked">&#128274;</span>
      `;
      group.appendChild(row);
      return group;
    }

    if (!this.nameEditing) {
      const hint = this.account.nameChangeFree
        ? `Your first name change is <span class="settings-name-cost free">free</span>`
        : `Change your name for <span class="settings-name-cost">${USERNAME_CHANGE_GOLD_COST} Gold</span>`;
      const row = document.createElement('button');
      row.className = 'settings-row settings-action-row accent-gold';
      row.innerHTML = `
        <div class="settings-action-text">
          <span class="settings-row-label settings-name-preview">${this.escape(this.account.username)}</span>
          <span class="settings-row-hint">${hint}</span>
        </div>
        <span class="settings-name-change">Change</span>
      `;
      row.addEventListener('click', () => this.openNameEditor());
      group.appendChild(row);
      return group;
    }

    const wrap = document.createElement('div');
    wrap.className = 'settings-name-editor';

    const input = document.createElement('input');
    input.className = 'settings-input settings-name-input';
    input.type = 'text';
    // Grapheme cap is enforced authoritatively by sanitizeUsername; this UTF-16
    // maxlength is just a generous typing guard (emoji eat several units each).
    input.maxLength = 48;
    input.autocomplete = 'off';
    input.setAttribute('autocapitalize', 'off');
    input.setAttribute('autocorrect', 'off');
    input.spellcheck = false;
    input.value = this.nameDraft || this.account.username;
    input.placeholder = 'New name';

    const status = document.createElement('div');
    status.className = 'settings-name-status';

    const actions = document.createElement('div');
    actions.className = 'settings-name-actions';

    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'settings-btn settings-name-cancel';
    cancelBtn.textContent = 'Cancel';
    cancelBtn.addEventListener('click', () => this.closeNameEditor());

    const confirm = document.createElement('button');
    confirm.className = 'settings-btn settings-btn-gold settings-name-confirm';
    confirm.innerHTML = this.account.nameChangeFree
      ? `Change &middot; <span class="settings-name-free">FREE</span>`
      : `Change &middot; ${USERNAME_CHANGE_GOLD_COST}<img class="settings-name-coin" src="/assets/icons/currency/gold.svg" alt="Gold">`;
    confirm.addEventListener('click', () => this.submitName());

    input.addEventListener('input', () => this.handleNameInput());
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') this.submitName(); });

    this.nameInputEl = input;
    this.nameStatusEl = status;
    this.nameConfirmBtn = confirm;

    actions.appendChild(cancelBtn);
    actions.appendChild(confirm);
    wrap.appendChild(input);
    wrap.appendChild(status);
    wrap.appendChild(actions);
    group.appendChild(wrap);

    this.applyNameStatus();
    setTimeout(() => input.focus(), 60);
    return group;
  }

  private openNameEditor(): void {
    this.nameEditing = true;
    this.nameDraft = this.account.username;
    this.nameStatus = 'same';   // starts as the current name → confirm disabled
    this.nameSubmitting = false;
    if (this.activeTab === 'account') this.renderPanel();
  }

  private closeNameEditor(): void {
    this.usernameCheckCancel?.();
    if (this.nameSubmitTimer) { clearTimeout(this.nameSubmitTimer); this.nameSubmitTimer = null; }
    this.nameEditing = false;
    this.nameSubmitting = false;
    this.nameStatus = 'idle';
    this.nameDraft = '';
    this.nameInputEl = null;
    this.nameStatusEl = null;
    this.nameConfirmBtn = null;
    if (this.activeTab === 'account') this.renderPanel();
  }

  /** Sanitize the keystroke, then decide: invalid / same / fire availability. */
  private handleNameInput(): void {
    if (!this.nameInputEl) return;
    const raw = this.nameInputEl.value;
    const { username: clean, valid } = sanitizeUsername(raw);
    this.nameDraft = clean;

    if (!valid) {
      this.usernameCheckCancel?.();
      this.nameStatus = raw.trim().length === 0 ? 'idle' : 'invalid';
      this.applyNameStatus();
      return;
    }
    if (clean === this.account.username) {
      this.usernameCheckCancel?.();
      this.nameStatus = 'same';
      this.applyNameStatus();
      return;
    }
    // Block our default Player#### shape up front (server enforces too).
    if (isReservedUsername(clean)) {
      this.usernameCheckCancel?.();
      this.nameStatus = 'reserved';
      this.applyNameStatus();
      return;
    }
    this.nameStatus = 'checking';
    this.applyNameStatus();
    this.usernameCheck?.(clean, (available) => {
      // Guard against a verdict landing after the field moved on (belt and
      // braces — the debouncer already drops stale responses by id).
      if (this.nameDraft !== clean || !this.nameEditing) return;
      this.nameStatus = available ? 'available' : 'taken';
      this.applyNameStatus();
    });
  }

  /** Reflect nameStatus on the open editor's status line + confirm button. */
  private applyNameStatus(): void {
    const LABELS: Record<NameStatus, string> = {
      idle: '',
      checking: 'Checking...',
      available: 'Available',
      taken: 'Taken, try another',
      invalid: 'Pick a valid name',
      same: 'This is already your name',
      reserved: 'That name is reserved',
    };
    if (this.nameStatusEl) {
      this.nameStatusEl.className = `settings-name-status ${this.nameStatus}`;
      this.nameStatusEl.textContent = this.nameSubmitting ? 'Saving...' : LABELS[this.nameStatus];
    }
    if (this.nameConfirmBtn) {
      this.nameConfirmBtn.disabled = this.nameSubmitting || this.nameStatus !== 'available';
    }
  }

  private submitName(): void {
    if (this.nameSubmitting || this.nameStatus !== 'available') return;
    const name = this.nameDraft;
    if (!name) return;
    this.nameSubmitting = true;
    this.usernameCheckCancel?.();
    this.applyNameStatus();   // disables confirm + shows "Saving..."
    this.emit('account:change-username', { username: name });
    // If no USERNAME_CHANGE_RESULT comes back (server unreachable), re-enable
    // the editor so it never hangs on "Saving..." forever.
    if (this.nameSubmitTimer) clearTimeout(this.nameSubmitTimer);
    this.nameSubmitTimer = setTimeout(() => {
      if (!this.nameSubmitting) return;
      this.nameSubmitting = false;
      this.nameStatus = 'available'; // was available when submitted — allow retry
      this.applyNameStatus();
    }, 8000);
  }

  /** Collapsible "Blocked Players" manager. Tap the header to expand the list. */
  private renderBlocked(): HTMLElement {
    const group = this.group('Privacy');

    // Privacy Policy link (opens in the system browser). Required for store
    // submission and the natural top of the Privacy group.
    const policy = document.createElement('a');
    policy.className = 'settings-row settings-action-row accent-cyan';
    policy.href = `${LEGAL_BASE}/privacy`;
    policy.target = '_blank';
    policy.rel = 'noopener';
    // Strip the default anchor styling (blue text + underline) — this row should
    // read like the other action rows, not a hyperlink.
    policy.style.textDecoration = 'none';
    policy.style.color = 'inherit';
    policy.innerHTML = `
      <div class="settings-action-text">
        <span class="settings-row-label">Privacy Policy</span>
        <span class="settings-row-hint">How we handle your data</span>
      </div>
      <span class="settings-chevron">›</span>
    `;
    group.appendChild(policy);

    // Terms of Service link (same treatment as the policy link).
    const terms = document.createElement('a');
    terms.className = 'settings-row settings-action-row accent-cyan';
    terms.href = `${LEGAL_BASE}/terms`;
    terms.target = '_blank';
    terms.rel = 'noopener';
    terms.style.textDecoration = 'none';
    terms.style.color = 'inherit';
    terms.innerHTML = `
      <div class="settings-action-text">
        <span class="settings-row-label">Terms of Service</span>
        <span class="settings-row-hint">The rules of play</span>
      </div>
      <span class="settings-chevron">›</span>
    `;
    group.appendChild(terms);

    const header = document.createElement('button');
    header.className = 'settings-row settings-action-row accent-cyan';
    header.innerHTML = `
      <div class="settings-action-text">
        <span class="settings-row-label">Blocked Players</span>
        <span class="settings-row-hint">${this.blocked.length} blocked</span>
      </div>
      <span class="settings-chevron">${this.blockedExpanded ? '⌄' : '›'}</span>
    `;
    header.addEventListener('click', () => {
      this.blockedExpanded = !this.blockedExpanded;
      if (this.activeTab === 'account') this.renderPanel();
    });
    group.appendChild(header);

    if (!this.blockedExpanded) return group;

    const list = document.createElement('div');
    list.className = 'settings-blocked-list';

    if (this.blocked.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'settings-blocked-empty';
      empty.textContent = "You haven't blocked anyone.";
      list.appendChild(empty);
    } else {
      for (const b of this.blocked) {
        const row = document.createElement('div');
        row.className = 'settings-blocked-row';

        const avatar = b.picture
          ? `<img class="settings-blocked-avatar" src="${b.picture}" alt="">`
          : `<div class="settings-blocked-avatar">${this.initials(b.username)}</div>`;

        const unblock = document.createElement('button');
        unblock.className = 'settings-blocked-unblock';
        unblock.textContent = 'UNBLOCK';
        unblock.addEventListener('click', () => this.emit('account:unblock', { playerId: b.id }));

        row.innerHTML = `${avatar}<span class="settings-blocked-name">${this.escape(b.username)}</span>`;
        row.appendChild(unblock);
        list.appendChild(row);
      }
    }
    group.appendChild(list);
    return group;
  }

  // ── Support ────────────────────────────────────────────────────────────

  private renderSupport(): void {
    if (this.supportThread) { this.renderTicketThread(this.supportThread); return; }
    this.renderSupportHome();
  }

  private renderSupportHome(): void {
    // ── New ticket ──
    const ticket = this.group('Get Help');
    const subject = document.createElement('input');
    subject.className = 'settings-input';
    subject.type = 'text';
    subject.maxLength = 80;
    subject.placeholder = 'Subject';
    const body = document.createElement('textarea');
    body.className = 'settings-textarea';
    body.maxLength = 1000;
    body.placeholder = 'Describe your issue…';
    const attachRow = this.buildAttachRow('new');
    const send = document.createElement('button');
    send.className = 'settings-btn settings-btn-gold';
    send.textContent = 'Open Ticket';
    send.addEventListener('click', () => {
      void this.handleCreateTicket(subject, body, send);
    });
    ticket.appendChild(subject);
    ticket.appendChild(body);
    ticket.appendChild(attachRow);
    ticket.appendChild(send);
    this.panel.appendChild(ticket);

    // ── My tickets ──
    if (this.myTickets.length > 0) {
      const mine = this.group('Your Tickets');
      const list = document.createElement('div');
      list.className = 'settings-tickets';
      for (const t of this.myTickets) {
        list.appendChild(this.buildTicketRow(t));
      }
      mine.appendChild(list);
      this.panel.appendChild(mine);
    }

    // ── Feedback ──
    const fb = this.group('Feedback');
    const fbBody = document.createElement('textarea');
    fbBody.className = 'settings-textarea';
    fbBody.maxLength = 1000;
    fbBody.placeholder = 'Tell us what you think…';
    const fbSend = document.createElement('button');
    fbSend.className = 'settings-btn settings-btn-cyan';
    fbSend.textContent = 'Send Feedback';
    fbSend.addEventListener('click', () => {
      void this.handleSendFeedback(fbBody, fbSend);
    });
    fb.appendChild(fbBody);
    fb.appendChild(fbSend);
    this.panel.appendChild(fb);

    // ── Rate ──
    const rate = this.group('Enjoying the game?');
    const rateBtn = document.createElement('button');
    rateBtn.className = 'settings-btn settings-btn-cyan';
    rateBtn.textContent = 'Leave a Rating';
    rateBtn.addEventListener('click', () => {
      this.emit('support:rate', undefined);
      this.flash(rateBtn, 'Thanks ✓');
    });
    rate.appendChild(rateBtn);
    this.panel.appendChild(rate);

    const about = document.createElement('div');
    about.className = 'settings-about';
    about.innerHTML = `
      <div class="settings-about-version">Street Dice Legends · v${this.escape(APP_VERSION)}</div>
      <div class="settings-about-links">
        <a href="${LEGAL_BASE}/privacy" target="_blank" rel="noopener">Privacy</a>
        <span>·</span>
        <a href="${LEGAL_BASE}/terms" target="_blank" rel="noopener">Terms</a>
      </div>
    `;
    this.panel.appendChild(about);
  }

  /** A single row in the "Your Tickets" list. */
  private buildTicketRow(t: TicketSummary): HTMLElement {
    const row = document.createElement('button');
    row.className = 'settings-ticket-row';
    const dot = t.unreadForPlayer ? '<span class="settings-ticket-dot"></span>' : '';
    row.innerHTML = `
      ${dot}
      <span class="settings-ticket-subject">${this.escape(t.subject)}</span>
      <span class="settings-ticket-status settings-ticket-status-${t.status}">${TICKET_STATUS_LABEL[t.status]}</span>
    `;
    row.addEventListener('click', () => { void this.openThread(t.id); });
    return row;
  }

  // ── Ticket thread view ──────────────────────────────────────────────────

  private renderTicketThread(t: TicketDetail): void {
    const head = document.createElement('div');
    head.className = 'settings-thread-head';
    const back = document.createElement('button');
    back.className = 'settings-thread-back';
    back.textContent = '‹ Back';
    back.addEventListener('click', () => this.closeThread());
    const title = document.createElement('div');
    title.className = 'settings-thread-title';
    title.innerHTML = `<span>${this.escape(t.subject)}</span>
      <span class="settings-ticket-status settings-ticket-status-${t.status}">${TICKET_STATUS_LABEL[t.status]}</span>`;
    head.appendChild(back);
    head.appendChild(title);
    this.panel.appendChild(head);

    const thread = document.createElement('div');
    thread.className = 'settings-thread';
    for (const m of t.messages) thread.appendChild(this.buildMessageBubble(m));
    this.panel.appendChild(thread);

    if (t.status === 'closed') {
      const note = document.createElement('div');
      note.className = 'settings-thread-note';
      note.textContent = 'This ticket is closed.';
      this.panel.appendChild(note);

      const reopen = document.createElement('button');
      reopen.className = 'settings-btn settings-btn-cyan';
      reopen.textContent = 'Reopen Ticket';
      reopen.addEventListener('click', () => { void this.handleReopen(t.id); });
      this.panel.appendChild(reopen);
      return;
    }

    // Reply box
    const replyGroup = this.group('Reply');
    const replyBody = document.createElement('textarea');
    replyBody.className = 'settings-textarea';
    replyBody.maxLength = 4000;
    replyBody.placeholder = 'Write a reply…';
    const attachRow = this.buildAttachRow('reply');
    const sendReply = document.createElement('button');
    sendReply.className = 'settings-btn settings-btn-gold';
    sendReply.textContent = 'Send Reply';
    sendReply.addEventListener('click', () => { void this.handleReply(t.id, replyBody, sendReply); });
    replyGroup.appendChild(replyBody);
    replyGroup.appendChild(attachRow);
    replyGroup.appendChild(sendReply);
    this.panel.appendChild(replyGroup);

    const close = document.createElement('button');
    close.className = 'settings-btn settings-btn-ghost';
    close.textContent = 'Close Ticket';
    close.addEventListener('click', () => { void this.handleClose(t.id); });
    this.panel.appendChild(close);
  }

  private buildMessageBubble(m: TicketDetail['messages'][number]): HTMLElement {
    const isAdmin = m.authorRole === 'admin';
    const bubble = document.createElement('div');
    bubble.className = `settings-msg settings-msg-${isAdmin ? 'admin' : 'player'}`;
    const who = isAdmin ? 'Support' : 'You';
    const when = new Date(m.createdAt).toLocaleString();
    const bodyHtml = m.body ? `<div class="settings-msg-body">${this.escape(m.body)}</div>` : '';
    let mediaHtml = '';
    for (const a of m.attachments ?? []) {
      mediaHtml += a.kind === 'video'
        ? `<video class="settings-msg-media" src="${this.escape(a.url)}" controls playsinline></video>`
        : `<a href="${this.escape(a.url)}" target="_blank" rel="noopener"><img class="settings-msg-media" src="${this.escape(a.url)}" alt="attachment"></a>`;
    }
    bubble.innerHTML = `
      <div class="settings-msg-meta">${who} · ${this.escape(when)}</div>
      ${bodyHtml}
      ${mediaHtml ? `<div class="settings-msg-media-row">${mediaHtml}</div>` : ''}
    `;
    return bubble;
  }

  // ── Support actions ──────────────────────────────────────────────────────

  private async handleCreateTicket(subject: HTMLInputElement, body: HTMLTextAreaElement, btn: HTMLButtonElement): Promise<void> {
    if (!this.supportToken) { showAnchoredToast(btn, 'Sign in to open a ticket.', { type: 'error' }); return; }
    const subj = subject.value.trim();
    const m = body.value.trim();
    // Accept the ticket if there's a subject OR details OR an attachment —
    // players often write the whole thing in the subject line.
    if (subj.length === 0 && m.length === 0 && this.newTicketFiles.length === 0) {
      showAnchoredToast(btn, 'Add a subject or some details first.', { type: 'error' });
      return;
    }
    btn.disabled = true;
    const created = await createTicket(
      this.supportToken,
      subj || '(no subject)',
      m,
      this.newTicketFiles.map((f) => f.dataUrl),
    );
    btn.disabled = false;
    if (!created) {
      showAnchoredToast(btn, 'Could not open your ticket. Please try again.', { type: 'error' });
      return;
    }
    subject.value = '';
    body.value = '';
    this.newTicketFiles = [];
    await this.refreshTickets();
    if (this.activeTab === 'support') this.renderPanel();
    showToast('Ticket opened. We will reply soon.', { type: 'success' });
  }

  private async handleSendFeedback(body: HTMLTextAreaElement, btn: HTMLButtonElement): Promise<void> {
    if (!this.supportToken) { showAnchoredToast(btn, 'Sign in to send feedback.', { type: 'error' }); return; }
    const m = body.value.trim();
    if (m.length < 5) { showAnchoredToast(btn, 'Please write a bit more before sending.', { type: 'error' }); return; }
    btn.disabled = true;
    const ok = await submitFeedback(this.supportToken, m, {
      appVersion: APP_VERSION,
      platform: Capacitor.getPlatform(),       // 'ios' | 'android' | 'web'
      deviceInfo: navigator.userAgent,          // model/OS hints; server truncates
    });
    btn.disabled = false;
    if (!ok) {
      showAnchoredToast(btn, 'Could not send your feedback. Please try again.', { type: 'error' });
      return;
    }
    body.value = '';
    this.flash(btn, 'Thanks ✓');
    showToast('Thanks! Your feedback was sent.', { type: 'success' });
  }

  private async openThread(id: string): Promise<void> {
    if (!this.supportToken) return;
    const detail = await getTicket(this.supportToken, id);
    if (!detail) return;
    this.supportThread = detail;
    this.replyFiles = [];
    // Opening clears the unread flag server-side; reflect it locally too.
    const summary = this.myTickets.find((t) => t.id === id);
    if (summary) summary.unreadForPlayer = false;
    this.updateSupportTabBadge();
    this.emit('support:unread-changed', { hasUnread: this.hasUnreadTickets() });
    this.renderPanel();
  }

  private closeThread(): void {
    this.supportThread = null;
    this.replyFiles = [];
    this.renderPanel();
  }

  private async handleReply(id: string, body: HTMLTextAreaElement, btn: HTMLButtonElement): Promise<void> {
    if (!this.supportToken) return;
    const m = body.value.trim();
    if (m.length < 1 && this.replyFiles.length === 0) { showAnchoredToast(btn, 'Write a reply first.', { type: 'error' }); return; }
    btn.disabled = true;
    const updated = await replyTicket(this.supportToken, id, m, this.replyFiles.map((f) => f.dataUrl));
    btn.disabled = false;
    if (!updated) {
      showAnchoredToast(btn, 'Could not send your reply. Please try again.', { type: 'error' });
      return;
    }
    this.supportThread = updated;
    this.replyFiles = [];
    void this.refreshTickets();
    this.renderPanel();
    showToast('Reply sent.', { type: 'success' });
  }

  private async handleClose(id: string): Promise<void> {
    if (!this.supportToken) return;
    const updated = await closeTicket(this.supportToken, id);
    if (updated) this.supportThread = updated;
    void this.refreshTickets();
    this.renderPanel();
  }

  private async handleReopen(id: string): Promise<void> {
    if (!this.supportToken) return;
    const updated = await reopenTicket(this.supportToken, id);
    if (updated) this.supportThread = updated;
    void this.refreshTickets();
    this.renderPanel();
  }

  // ── Attachments ──────────────────────────────────────────────────────────

  /** Build the "add screenshot / recording" control + chip strip for a target. */
  private buildAttachRow(target: 'new' | 'reply'): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'settings-attach';

    const chips = document.createElement('div');
    chips.className = 'settings-attach-chips';
    this.renderAttachChips(chips, target);

    const add = document.createElement('button');
    add.className = 'settings-attach-add';
    add.type = 'button';
    add.textContent = '+ Add screenshot or recording';
    add.addEventListener('click', () => { void this.pickAttachments(target, chips); });

    wrap.appendChild(chips);
    wrap.appendChild(add);
    return wrap;
  }

  private renderAttachChips(container: HTMLElement, target: 'new' | 'reply'): void {
    container.innerHTML = '';
    const files = target === 'new' ? this.newTicketFiles : this.replyFiles;
    files.forEach((f, idx) => {
      const chip = document.createElement('span');
      chip.className = 'settings-attach-chip';
      chip.innerHTML = `<span>${f.kind === 'video' ? '🎬' : '🖼'} ${this.escape(f.name)}</span>`;
      const x = document.createElement('button');
      x.className = 'settings-attach-x';
      x.type = 'button';
      x.textContent = '×';
      x.addEventListener('click', () => {
        files.splice(idx, 1);
        this.renderAttachChips(container, target);
      });
      chip.appendChild(x);
      container.appendChild(chip);
    });
  }

  /** Open the system file picker, validate, encode to data URLs, store as drafts. */
  private async pickAttachments(target: 'new' | 'reply', chips: HTMLElement): Promise<void> {
    const files = target === 'new' ? this.newTicketFiles : this.replyFiles;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,video/*';
    input.multiple = true;
    input.style.display = 'none';
    document.body.appendChild(input);

    const picked: File[] = await new Promise((resolve) => {
      let done = false;
      const finish = (files: File[]) => { if (!done) { done = true; resolve(files); } };
      input.addEventListener('change', () => finish(input.files ? Array.from(input.files) : []), { once: true });
      // Cancel detection: closing the dialog refocuses the window. If no change
      // fired shortly after, the user cancelled — resolve empty so we don't hang.
      window.addEventListener('focus', () => { setTimeout(() => finish([]), 350); }, { once: true });
      input.click();
    });
    input.remove();

    for (const file of picked) {
      if (files.length >= MAX_ATTACHMENTS) break;
      const kind: 'image' | 'video' = file.type.startsWith('video/') ? 'video' : 'image';
      const cap = kind === 'video' ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES;
      if (file.size > cap) continue; // silently skip oversize; chip count makes it obvious
      const dataUrl = await this.fileToDataUrl(file);
      if (dataUrl) files.push({ dataUrl, name: file.name.slice(0, 40), kind });
    }
    this.renderAttachChips(chips, target);
  }

  private fileToDataUrl(file: File): Promise<string | null> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });
  }

  /** Toggle the unread dot on the Support tab button. */
  private updateSupportTabBadge(): void {
    const tabBtn = this.tabBar?.querySelector<HTMLElement>('.settings-tab[data-tab="support"]');
    if (!tabBtn) return;
    tabBtn.classList.toggle('has-badge', this.hasUnreadTickets());
  }

  // ── Reusable control builders ────────────────────────────────────────────

  private group(title: string): HTMLElement {
    const el = document.createElement('div');
    el.className = 'settings-group';
    if (title) {
      const h = document.createElement('div');
      h.className = 'settings-group-title';
      h.textContent = title;
      el.appendChild(h);
    }
    return el;
  }

  private sliderRow(label: string, value: number, disabled: boolean, onChange: (v: number) => void): HTMLElement {
    const row = document.createElement('div');
    row.className = 'settings-row settings-slider-row';
    if (disabled) row.classList.add('disabled');

    const name = document.createElement('span');
    name.className = 'settings-row-label';
    name.textContent = label;

    const pct = document.createElement('span');
    pct.className = 'settings-slider-val';
    pct.textContent = `${Math.round(value * 100)}%`;

    const range = document.createElement('input');
    range.className = 'settings-range';
    range.type = 'range';
    range.min = '0';
    range.max = '100';
    range.value = String(Math.round(value * 100));
    range.disabled = disabled;
    range.addEventListener('input', () => {
      const v = Number(range.value) / 100;
      pct.textContent = `${range.value}%`;
      onChange(v);
    });

    const head = document.createElement('div');
    head.className = 'settings-slider-head';
    head.appendChild(name);
    head.appendChild(pct);

    row.appendChild(head);
    row.appendChild(range);
    return row;
  }

  private toggleRow(label: string, value: boolean, onChange: (v: boolean) => void): HTMLElement {
    const row = document.createElement('div');
    row.className = 'settings-row';

    const name = document.createElement('span');
    name.className = 'settings-row-label';
    name.textContent = label;

    const sw = document.createElement('button');
    sw.className = 'settings-switch';
    sw.classList.toggle('on', value);
    sw.setAttribute('role', 'switch');
    sw.setAttribute('aria-checked', String(value));
    sw.innerHTML = '<span class="settings-switch-knob"></span>';
    sw.addEventListener('click', () => {
      const next = !sw.classList.contains('on');
      sw.classList.toggle('on', next);
      sw.setAttribute('aria-checked', String(next));
      onChange(next);
    });

    row.appendChild(name);
    row.appendChild(sw);
    return row;
  }

  private actionRow(label: string, hint: string, accent: 'cyan' | 'gold' | 'danger', onClick: () => void): HTMLElement {
    const row = document.createElement('button');
    row.className = `settings-row settings-action-row accent-${accent}`;

    const left = document.createElement('div');
    left.className = 'settings-action-text';
    left.innerHTML = `<span class="settings-row-label">${this.escape(label)}</span>${hint ? `<span class="settings-row-hint">${this.escape(hint)}</span>` : ''}`;

    const chevron = document.createElement('span');
    chevron.className = 'settings-chevron';
    chevron.textContent = '›';

    row.appendChild(left);
    row.appendChild(chevron);
    row.addEventListener('click', onClick);
    return row;
  }

  private flash(btn: HTMLButtonElement, text: string): void {
    const original = btn.textContent;
    btn.textContent = text;
    btn.classList.add('flashing');
    setTimeout(() => {
      btn.textContent = original;
      btn.classList.remove('flashing');
    }, 1400);
  }

  private initials(name: string): string {
    return name.trim().slice(0, 2).toUpperCase() || '?';
  }

  private escape(s: string): string {
    const div = document.createElement('div');
    div.textContent = s;
    return div.innerHTML;
  }
}
